import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions?: string[];
  userType?: string | undefined;                // "external" or "internal"
  organizationId?: string | undefined;
  organizationName?: string | undefined; // Optional: Store organization name for convenience
  mobileNumber?: string | undefined;
  serviceProviderId?: string | undefined;
  authorizedStates?: string[] | undefined;
  authorizedDistricts?: string[] | undefined;
  authorizedDispatchAreas?: string[] | undefined;
  lastLogin?: Date | undefined;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  token: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User }>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      // Token is managed via HttpOnly cookies, no need to store in Redux
      state.token = null;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setFullUserProfile: (state, action: PayloadAction<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      userRoles: string[];
      permissions?: string[] | undefined;
      userType?: string | undefined;
      organizationId?: string | undefined;
      organizationName?: string | undefined;
      mobileNumber?: string | undefined;
      serviceProviderId?: string | undefined;
      authorizedStates?: string[] | undefined;
      authorizedDistricts?: string[] | undefined;
      authorizedDispatchAreas?: string[] | undefined;
    }>) => {
      state.user = {
        id: action.payload.id,
        email: action.payload.email,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        roles: action.payload.userRoles || [], // Ensure it's never undefined
        permissions: action.payload.permissions || [],
        userType: action.payload.userType || 'external',
        organizationId: action.payload.organizationId || undefined,
        organizationName: action.payload.organizationName || undefined, // Organization name from User Service
        mobileNumber: action.payload.mobileNumber || undefined,
        serviceProviderId: action.payload.serviceProviderId || undefined,
        authorizedStates: action.payload.authorizedStates || [],
        authorizedDistricts: action.payload.authorizedDistricts || [],
        authorizedDispatchAreas: action.payload.authorizedDispatchAreas || [],
      };
      state.isAuthenticated = true;
      state.isLoading = false; // Important: Set loading to false
      state.error = null; // Clear any errors
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  updateUser,
  setFullUserProfile,
} = authSlice.actions;

export default authSlice.reducer;